import { useState, useEffect, useRef, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { AuthContext } from "../../context/AuthContext.js";
import { useNavigate } from "react-router-dom";
import {
  FaGraduationCap,
  FaVenusMars,
  FaBuilding,
  FaIdBadge,
  FaPhone,
  FaCog,
  // FaEnvelope,
  FaCamera,
  FaTrashAlt,
  FaTrophy,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";
import {
  apiDeleteImage,
  apiUploadImage,
  getUser,
} from "../../services/userService.js";
import api from "../../services/api.js";
import FullPageLoader from "../../components/ui/fullPageLoader.js";
import PopupMessage from "../../components/common/PopupMessage.js";

export default function UserProfile() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [score, setScore] = useState(0);

  const fetchUserAndPoints = async () => {
    try {
      setLoading(true);
      const res = await api.get("/users/my-profile");
      setScore(res?.data?.data?.score || 0);
      setUserData(res.data?.data);
    } catch (err) {
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAndPoints();
  }, []);

  if (!user)
    return <div className="text-center mt-5 text-danger">User not found</div>;
  if (loading) return <FullPageLoader loadingName="Loading Profile..." />;

  const coverGradient =
    userData?.gender === "FEMALE"
      ? "linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)"
      : "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)";

  return (
    <div
      className="min-vh-100 page-container"
      style={{ backgroundColor: "#f3f5f9" }}
    >
      {/* --- 1. Hero Cover --- */}
      <div className="profile-cover" style={{ background: coverGradient }}>
        <div className="cover-pattern"></div>
      </div>

      <div className="container profile-content">
        {/* Main Profile Card */}
        <div className="card border-0 shadow-lg rounded-4 overflow-visible mb-4 animate-up">
          <div className="card-body p-0">
            {/* Header Content */}
            <div className="text-center">
              {/* Avatar Wrapper */}
              <div className="avatar-container">
                <div className="p-1 bg-white rounded-circle shadow-sm d-inline-block">
                  <ProfileImageUploader
                    currentImg={userData?.profileImage?.secure_url}
                    gender={userData?.gender}
                  />
                </div>
              </div>

              {/* Name & Title */}
              <div className="mt-3 mb-4 px-3">
                <h2 className="fw-bold text-dark mb-1 name-text">
                  {userData?.fullName}
                </h2>
                <span className="bg-primary-subtle text-primary rounded-pill px-3 py-2 fw-semibold text-wrap lh-base mt-1 d-inline-block">
                  {userData?.role || "Student"} @ {userData?.faculty}
                </span>
                <p className="text-muted mt-2 mb-0 small">{userData?.email}</p>
              </div>
            </div>

            <hr className="my-0 opacity-10" />

            {/* Actions Bar */}
            <div className="actions-bar bg-light rounded-bottom-4">
              {user?.role !== "ADMIN" && (
                <button
                  onClick={() =>
                    navigate("/user/profile/pointsHistory", {
                      state: { userId: user._id },
                    })
                  }
                  className="btn btn-white border shadow-sm rounded-pill fw-semibold action-btn hover-scale"
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaTrophy className="text-warning" size={18} />
                    <span>{score} Points</span>
                    <FaChevronRight size={12} className="text-muted ms-1" />
                  </div>
                </button>
              )}

              {user?.role !== "ADMIN" && (
                <button
                  onClick={() => navigate("/role-requests")}
                  className="btn btn-white rounded-pill fw-semibold action-btn hover-scale"
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaUser size={16} /> role requests
                  </div>
                </button>
              )}

              <button
                onClick={() => navigate("/user/settings")}
                className="btn btn-dark rounded-pill fw-semibold action-btn hover-scale"
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <FaCog size={16} /> Settings
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* --- 2. Info Grid --- */}
        <div className="row g-4">
          {/* Left Column: Academic Card (UPDATED LAYOUT) */}
          {user?.role !== "ADMIN" && (
            <div
              className="col-lg-5 animate-up"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                  <h5 className="fw-bold text-nowrap text-dark mb-0 d-flex align-items-center gap-2">
                    <div className="icon-badge bg-primary-subtle text-primary">
                      <FaIdBadge />
                    </div>
                    Academic Details
                  </h5>
                </div>
                <div className="card-body p-4 d-flex flex-column gap-3">
                  {/* 1. University ID */}
                  <div
                    className="p-3 rounded-4 d-flex align-items-center gap-3"
                    style={{
                      backgroundColor: "#eef2ff",
                      borderLeft: "5px solid #4f46e5",
                    }}
                  >
                    <div className="text-primary fs-4">
                      <FaIdBadge />
                    </div>
                    <div>
                      <small
                        className="text-uppercase text-muted fw-bold"
                        style={{ fontSize: "0.7rem", letterSpacing: "1px" }}
                      >
                        University ID
                      </small>
                      <div className="fw-bold fs-5 text-dark font-monospace">
                        {userData?.universtyID || "N/A"}
                      </div>
                    </div>
                  </div>
                  {/* 2. Level */}
                  <div
                    className="p-3 rounded-4 d-flex align-items-center gap-3"
                    style={{
                      backgroundColor: "#fffbeb",
                      borderLeft: "5px solid #f59e0b",
                    }}
                  >
                    <div className="text-warning fs-4">
                      <FaGraduationCap />
                    </div>
                    <div>
                      <small
                        className="text-uppercase text-muted fw-bold"
                        style={{ fontSize: "0.7rem", letterSpacing: "1px" }}
                      >
                        Current Level
                      </small>
                      <div className="fw-bold text-dark">
                        {userData?.level?.replace("LEVEL_", "Level ")}
                      </div>
                    </div>
                  </div>
                  {/* 3. Faculty */}
                  <div
                    className="p-3 rounded-4 d-flex align-items-center gap-3"
                    style={{
                      backgroundColor: "#f3e8ff",
                      borderLeft: "5px solid #9333ea",
                    }}
                  >
                    <div style={{ color: "#9333ea" }} className="fs-4">
                      <FaBuilding />
                    </div>
                    <div className="flex-grow-1">
                      <small
                        className="text-uppercase text-muted fw-bold"
                        style={{ fontSize: "0.7rem", letterSpacing: "1px" }}
                      >
                        Faculty
                      </small>
                      <div
                        className="fw-bold text-dark text-break"
                        style={{ lineHeight: "1.3" }}
                      >
                        {userData?.faculty}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Right Column: Personal Info (UPDATED ICONS & HEADER) */}
          <div
            className="col-lg-7 animate-up"
            style={{ animationDelay: "0.2s" }}
          >
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                {/* Fixed Header on Mobile */}
                <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2 personal-info-header">
                  <div className="icon-badge bg-primary text-light">
                    <FaUser />
                  </div>
                  <span className="text-nowrap">Personal Information</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <ul className="list-group list-group-flush">
                  <InfoRow
                    icon={<FaPhone />}
                    colorClass="bg-primary-subtle text-primary"
                    label="Phone"
                    value={userData?.phone}
                  />
                  <InfoRow
                    icon={<FaVenusMars />}
                    colorClass="bg-primary-subtle text-primary"
                    label="Gender"
                    value={userData?.gender}
                  />
                  <InfoRow
                    icon={<FaGraduationCap />}
                    colorClass="bg-primary-subtle text-primary"
                    label="Account Status"
                    value={
                      <span className="bg-success-subtle text-success rounded-pill px-1">
                        Active
                      </span>
                    }
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Styles --- */}
      <style>{`
        /* General Layout */
        .page-container { margin-top: 60px; padding-bottom: 50px; }
        .profile-cover { height: 220px; position: relative; }
        .cover-pattern { position: absolute; width: 100%; height: 100%; background: url('https://www.transparenttextures.com/patterns/cubes.png'); opacity: 0.1; }
        .profile-content { margin-top: -100px; }
        .avatar-container { margin-top: -75px; position: relative; }

        /* Icon Badge in Headers */
        .icon-badge {
            width: 32px; height: 32px; 
            border-radius: 8px; 
            display: flex; align-items: center; justify-content: center;
            font-size: 1rem;
        }

        /* Actions Bar */
        .actions-bar { display: flex; justify-content: flex-end; gap: 1rem; padding: 1rem; }
        .action-btn { padding: 0.5rem 1.5rem; min-width: 140px; }
        .btn-white { background: white; }
        .btn-white:hover { background: #f8f9fa; }

        /* Animations */
        .animate-up { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(20px); }
        @keyframes fadeInUp { to { opacity: 1; transform: translateY(0); } }
        .hover-scale { transition: transform 0.2s; }
        .hover-scale:hover { transform: translateY(-2px); }

        /* --- Mobile Responsiveness --- */
        @media (max-width: 768px) {
          .profile-cover { height: 160px; }
          .profile-content { margin-top: -60px; }
          .avatar-container { margin-top: -50px; }
          .name-text { font-size: 1.5rem; }
          
          /* Personal Info Header Sizing */
          .personal-info-header { font-size: 1.1rem; }

          /* Stack buttons */
          .actions-bar { flex-direction: column; gap: 0.8rem; }
          .action-btn { width: 100%; padding: 0.8rem; }
          
          .col-lg-5, .col-lg-7 { margin-bottom: 0; }
        }
      `}</style>
    </div>
  );
}

// --- Improved Helper Component (With Colors) ---
const InfoRow = ({ icon, label, value, colorClass }) => (
  <li className="list-group-item px-0 py-3 border-light d-flex align-items-center justify-content-between">
    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
      {/* Colored Icon Wrapper */}
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${colorClass}`}
        style={{ width: "42px", height: "42px", fontSize: "1.2rem" }}
      >
        {icon}
      </div>
      <span className="text-muted fw-medium text-truncate">{label}</span>
    </div>
    <span
      className="fw-semibold text-dark text-end text-break"
      style={{ maxWidth: "60%", lineHeight: "1.4" }}
    >
      {value || "Not Set"}
    </span>
  </li>
);

/* ===============================================
      INTERNAL COMPONENT: ProfileImageUploader
      (Logic Preserved)
================================================ */

function ProfileImageUploader({ currentImg, gender }) {
  const { setUser } = useContext(AuthContext);

  const defaultAvatar =
    gender === "MALE"
      ? "/avatars/MALE_avatar.png"
      : "/avatars/FEMALE_avatar.png";

  const [imgSrc, setImgSrc] = useState(currentImg || defaultAvatar);
  const [showModal, setShowModal] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const fileInputRef = useRef();

  const isProcessing = loadingUpload || loadingDelete;

  useEffect(() => {
    setImgSrc(currentImg || defaultAvatar);
  }, [currentImg, defaultAvatar]);

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoadingUpload(true);
    try {
      const response = await apiUploadImage(file);
      setPopup({ show: true, type: "success", message: response.message });
      setImgSrc(URL.createObjectURL(file));
      const updatedUser = await getUser();
      setUser(updatedUser.data || updatedUser.data?.data);
      setShowModal(false);
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: error.message || "Failed to upload image",
      });
    } finally {
      setLoadingUpload(false);
    }
  };

  const deleteImage = async () => {
    setLoadingDelete(true);
    try {
      const response = await apiDeleteImage();
      setPopup({ show: true, type: "info", message: response.message });
      setImgSrc(defaultAvatar);
      const updatedUser = await getUser();
      setUser(updatedUser?.data || updatedUser?.data?.data);
      setShowModal(false);
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: "Failed to delete image",
      });
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleCloseModal = () => {
    if (!isProcessing) setShowModal(false);
  };

  return (
    <div className="position-relative group">
      <img
        src={imgSrc}
        alt="avatar"
        className="rounded-circle object-fit-cover shadow-sm"
        style={{ width: "150px", height: "150px", border: "5px solid white" }}
        onError={(e) => {
          e.target.src = defaultAvatar;
        }}
      />

      <button
        onClick={() => setShowModal(true)}
        className="position-absolute bottom-0 end-0 btn btn-primary rounded-circle border border-white shadow-sm d-flex align-items-center justify-content-center p-0"
        style={{
          width: "42px",
          height: "42px",
          transform: "translate(-5px, -5px)",
        }}
        title="Change Picture"
      >
        <FaCamera size={18} />
      </button>

      {/* Modal */}
      {showModal && (
        <div
          className="custom-modal-overlay rounded-4"
          onClick={handleCloseModal}
        >
          <div
            className="custom-modal bg-white text-dark shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="mb-4 fw-bold bg-primary-subtle text-primary p-1 rounded-4">
              Update Photo
            </h5>
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-primary py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                onClick={() => fileInputRef.current.click()}
                disabled={isProcessing}
              >
                {loadingUpload ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <FaCamera />
                )}
                {loadingUpload ? "Uploading..." : "Upload New"}
              </button>
              <input
                type="file"
                hidden
                accept="image/*"
                ref={fileInputRef}
                onChange={uploadImage}
                disabled={isProcessing}
              />

              <button
                className="btn btn-outline-danger py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                onClick={deleteImage}
                disabled={isProcessing}
              >
                {loadingDelete ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <FaTrashAlt />
                )}
                {loadingDelete ? "Deleting..." : "Remove"}
              </button>

              <button
                className="btn btn-light text-muted mt-2"
                onClick={handleCloseModal}
                disabled={isProcessing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}

      <style>{`
        .custom-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(2px); display: flex; justify-content: center; align-items: center; z-index: 2000; cursor: pointer; }
        .custom-modal { padding: 2rem; border-radius: 1rem; width: 90%; max-width: 320px; animation: popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); cursor: default; }
        @keyframes popIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}

// import { useState, useEffect, useRef, useContext } from "react";
// import "bootstrap/dist/css/bootstrap.min.css";
// import { AuthContext } from "../../context/AuthContext.js";
// import { useNavigate } from "react-router-dom";
// import {
//   FaGraduationCap,
//   FaVenusMars,
//   FaBuilding,
//   FaIdBadge,
//   FaPhone,
//   FaCog,
//   // FaEnvelope,
//   FaCamera,
//   FaTrashAlt,
//   FaTrophy,
//   FaChevronRight,
// } from "react-icons/fa";
// import {
//   apiDeleteImage,
//   apiUploadImage,
//   getUser,
// } from "../../services/userService.js";
// import api from "../../services/api.js";
// import FullPageLoader from "../../components/ui/fullPageLoader.js";
// import PopupMessage from "../../components/common/PopupMessage.js";

// export default function UserProfile() {
//   const { user } = useContext(AuthContext);
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [userData, setUserData] = useState(null);
//   const [score, setScore] = useState(0);
//   console.log("userData: ",userData);

//   const fetchUserAndPoints = async () => {
//     try {
//       setLoading(true);
//       const res = await api.get("/users/my-profile");
//       setScore(res?.data?.data?.score || 0);
//       setUserData(res.data?.data);
//     } catch (err) {
//       setUserData(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchUserAndPoints();
//   }, []);

//   if (!user) return <div className="text-center mt-5 text-danger">User not found</div>;
//   if (loading) return <FullPageLoader loadingName="Loading Profile..." />;

//   const coverGradient =
//     userData?.gender === "FEMALE"
//       ? "linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)"
//       : "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)";

//   return (
//     <div className="min-vh-100 page-container" style={{ backgroundColor: "#f3f5f9" }}>

//       {/* --- 1. Hero Cover --- */}
//       <div className="profile-cover" style={{ background: coverGradient }}>
//         <div className="cover-pattern"></div>
//       </div>

//       <div className="container profile-content">
//         {/* Main Profile Card */}
//         <div className="card border-0 shadow-lg rounded-4 overflow-visible mb-4 animate-up">
//           <div className="card-body p-0">

//             {/* Header Content */}
//             <div className="text-center">
//                 {/* Avatar Wrapper */}
//                 <div className="avatar-container">
//                   <div className="p-1 bg-white rounded-circle shadow-sm d-inline-block">
//                     <ProfileImageUploader
//                       currentImg={userData?.profileImage?.secure_url}
//                       gender={userData?.gender}
//                     />
//                   </div>
//                 </div>

//                 {/* Name & Title */}
//                 <div className="mt-3 mb-4 px-3">
//                   <h2 className="fw-bold text-dark mb-1 name-text">
//                     {userData?.fullName}
//                   </h2>
//                   <span className="bg-primary-subtle text-primary rounded-pill px-2 py-1 fw-semibold text-wrap lh-base mt-1">
//                     {userData?.role || "Student"} @ {userData?.faculty}
//                   </span>
//                   <p className=" text-dark rounded-pill px-3 py-2 fw-semibold text-wrap lh-base mt-1">
//                     {userData?.email}
//                   </p>
//                   {/* <InfoRow
//                     icon={<FaEnvelope className="text-secondary" />}
//                     label="Email"
//                     value={userData?.email}
//                   /> */}
//                 </div>
//             </div>

//             <hr className="my-0 opacity-10" />

//             {/* Actions Bar (Responsive) */}
//             <div className="actions-bar bg-light rounded-bottom-4">
//               {user?.role !== "ADMIN" && (
//                 <button
//                   onClick={() =>
//                     navigate("/user/profile/pointsHistory", {
//                       state: { userId: user._id },
//                     })
//                   }
//                   className="btn btn-white border shadow-sm rounded-pill fw-semibold action-btn hover-scale"
//                 >
//                   <div className="d-flex align-items-center justify-content-center gap-2">
//                     <FaTrophy className="text-warning" size={18} />
//                     <span>{score} Points</span>
//                     <FaChevronRight size={12} className="text-muted ms-1" />
//                   </div>
//                 </button>
//               )}

//               <button
//                 onClick={() => navigate("/user/settings")}
//                 className="btn btn-dark rounded-pill fw-semibold action-btn hover-scale"
//               >
//                 <div className="d-flex align-items-center justify-content-center gap-2">
//                    <FaCog size={16} /> Settings
//                 </div>
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* --- 2. Info Grid --- */}
//         <div className="row g-4">
//           {/* Left Column: Academic Card */}
//           <div className="col-lg-5 animate-up" style={{ animationDelay: "0.1s" }}>
//             <div className="card border-0 shadow-sm rounded-4 h-100">
//               <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
//                 <h5 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2">
//                   <FaIdBadge /> Academic ID
//                 </h5>
//               </div>
//               <div className="card-body p-4">
//                 <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: "#f8f9fa", borderLeft: "4px solid #0d6efd" }}>
//                   <small className="text-muted d-block text-uppercase fw-bold" style={{ fontSize: "0.7rem" }}>
//                     University ID
//                   </small>
//                   <span className="fw-bold fs-5 text-dark font-monospace text-break">
//                     {userData?.universtyID || "N/A"}
//                   </span>
//                 </div>

//                 <div className="row g-3">
//                   <div className="col-6">
//                     <div className="p-3 rounded-3 bg-light h-100 text-center text-sm-start">
//                       <small className="text-muted d-block mb-1">Level</small>
//                       <div className="d-flex align-items-center justify-content-center justify-content-sm-start gap-2 flex-wrap">
//                         <FaGraduationCap className="text-primary" />
//                         <span className="fw-semibold">
//                           {userData?.level?.replace("LEVEL_", "")}
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="col-6">
//                     <div className="p-3 rounded-3 bg-light h-100 text-center text-sm-start">
//                       <small className="text-muted d-block mb-1">Faculty</small>
//                       <div className="d-flex align-items-center justify-content-center justify-content-sm-start gap-2 flex-wrap">
//                         <FaBuilding className="text-success" />
//                         <span className="fw-semibold text-truncate d-block w-100" title={userData?.faculty}>
//                           {userData?.faculty}
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Right Column: Personal Info */}
//           <div className="col-lg-7 animate-up" style={{ animationDelay: "0.2s" }}>
//             <div className="card border-0 shadow-sm rounded-4 h-100">
//               <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
//                 <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
//                   Personal Information
//                 </h5>
//               </div>
//               <div className="card-body p-4">
//                 <ul className="list-group list-group-flush">
//                   <InfoRow
//                     icon={<FaPhone className="text-secondary" />}
//                     label="Phone"
//                     value={userData?.phone}
//                   />
//                   <InfoRow
//                     icon={<FaVenusMars className="text-secondary" />}
//                     label="Gender"
//                     value={userData?.gender}
//                   />
//                   <InfoRow
//                     icon={<FaGraduationCap className="text-secondary" />}
//                     label="Status"
//                     value={<span className="bg-success-subtle text-success rounded-pill px-3">Active</span>}
//                   />
//                 </ul>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* --- Responsive Styles --- */}
//       <style>{`
//         /* General Layout */
//         .page-container { margin-top: 60px; padding-bottom: 50px; }
//         .profile-cover { height: 220px; position: relative; }
//         .cover-pattern { position: absolute; width: 100%; height: 100%; background: url('https://www.transparenttextures.com/patterns/cubes.png'); opacity: 0.1; }
//         .profile-content { margin-top: -100px; }
//         .avatar-container { margin-top: -75px; position: relative; }

//         /* Actions Bar */
//         .actions-bar { display: flex; justify-content: flex-end; gap: 1rem; padding: 1rem; }
//         .action-btn { padding: 0.5rem 1.5rem; min-width: 140px; }
//         .btn-white { background: white; }
//         .btn-white:hover { background: #f8f9fa; }

//         /* Animations */
//         .animate-up { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(20px); }
//         @keyframes fadeInUp { to { opacity: 1; transform: translateY(0); } }
//         .hover-scale { transition: transform 0.2s; }
//         .hover-scale:hover { transform: translateY(-2px); }

//         /* --- Mobile Responsiveness (The Fix) --- */
//         @media (max-width: 768px) {
//           .profile-cover { height: 160px; } /* Smaller cover on mobile */
//           .profile-content { margin-top: -60px; } /* Adjust overlap */
//           .avatar-container { margin-top: -50px; }
//           .name-text { font-size: 1.5rem; }

//           /* Stack buttons on mobile and make them full width */
//           .actions-bar { flex-direction: column; gap: 0.8rem; }
//           .action-btn { width: 100%; padding: 0.8rem; }

//           /* Adjust Grid */
//           .col-lg-5, .col-lg-7 { margin-bottom: 0; }
//         }
//       `}</style>
//     </div>
//   );
// }

// // --- Helper Component for List Rows (Improved Icons) ---
// const InfoRow = ({ icon, label, value }) => (
//   <li className="list-group-item px-0 py-3 border-light d-flex align-items-center justify-content-between">
//     <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
//       {/* Increased Icon Wrapper Size */}
//       <div
//         className="bg-light rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
//         style={{ width: "42px", height: "42px", fontSize: "1.2rem" }}
//       >
//         {icon}
//       </div>
//       <span className="text-muted fw-medium text-truncate">{label}</span>
//     </div>
//     <span
//       className="fw-semibold text-dark text-end text-break"
//       style={{ maxWidth: "60%", lineHeight: "1.4" }}
//     >
//       {value || "Not Set"}
//     </span>
//   </li>
// );

// /* ===============================================
//       INTERNAL COMPONENT: ProfileImageUploader
//       (Logic Preserved, Style Matched)
// ================================================ */

// function ProfileImageUploader({ currentImg, gender }) {
//   const { setUser } = useContext(AuthContext);

//   const defaultAvatar = gender === "MALE"
//      ? "/avatars/MALE_avatar.png"
//      : "/avatars/FEMALE_avatar.png";

//   const [imgSrc, setImgSrc] = useState(currentImg || defaultAvatar);
//   const [showModal, setShowModal] = useState(false);
//   const [loadingUpload, setLoadingUpload] = useState(false);
//   const [loadingDelete, setLoadingDelete] = useState(false);
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });
//   const fileInputRef = useRef();

//   const isProcessing = loadingUpload || loadingDelete;

//   useEffect(() => {
//      setImgSrc(currentImg || defaultAvatar);
//   }, [currentImg, defaultAvatar]);

//   const uploadImage = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;
//     setLoadingUpload(true);
//     try {
//       const response = await apiUploadImage(file);
//       setPopup({ show: true, type: "success", message: response.message });
//       setImgSrc(URL.createObjectURL(file));
//       const updatedUser = await getUser();
//       setUser(updatedUser.data || updatedUser.data?.data);
//       setShowModal(false);
//     } catch (error) {
//       console.error(error);
//       setPopup({ show: true, type: "error", message: "Failed to upload image" });
//     } finally {
//       setLoadingUpload(false);
//     }
//   };

//   const deleteImage = async () => {
//     setLoadingDelete(true);
//     try {
//       const response = await apiDeleteImage();
//       setPopup({ show: true, type: "info", message: response.message });
//       setImgSrc(defaultAvatar);
//       const updatedUser = await getUser();
//       setUser(updatedUser?.data || updatedUser?.data?.data);
//       setShowModal(false);
//     } catch (error) {
//       console.error(error);
//       setPopup({ show: true, type: "error", message: "Failed to delete image" });
//     } finally {
//       setLoadingDelete(false);
//     }
//   };

//   const handleCloseModal = () => {
//     if (!isProcessing) setShowModal(false);
//   };

//   return (
//     <div className="position-relative group">
//       <img
//         src={imgSrc}
//         alt="avatar"
//         className="rounded-circle object-fit-cover shadow-sm"
//         style={{ width: "150px", height: "150px", border: "5px solid white" }}
//         onError={(e) => { e.target.src = defaultAvatar; }}
//       />

//       <button
//         onClick={() => setShowModal(true)}
//         className="position-absolute bottom-0 end-0 btn btn-primary rounded-circle border border-white shadow-sm d-flex align-items-center justify-content-center p-0"
//         style={{ width: "42px", height: "42px", transform: "translate(-5px, -5px)" }}
//         title="Change Picture"
//       >
//         <FaCamera size={18} />
//       </button>

//       {/* Modal */}
//       {showModal && (
//         <div className="custom-modal-overlay" onClick={handleCloseModal}>
//           <div className="custom-modal bg-white text-dark shadow-lg" onClick={(e) => e.stopPropagation()}>
//             <h5 className="mb-4 fw-bold">Update Photo</h5>
//             <div className="d-grid gap-2">
//               <button
//                 className="btn btn-outline-primary py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
//                 onClick={() => fileInputRef.current.click()}
//                 disabled={isProcessing}
//               >
//                 {loadingUpload ? <span className="spinner-border spinner-border-sm" /> : <FaCamera />}
//                 {loadingUpload ? "Uploading..." : "Upload New"}
//               </button>
//               <input type="file" hidden accept="image/*" ref={fileInputRef} onChange={uploadImage} disabled={isProcessing} />

//               <button
//                 className="btn btn-outline-danger py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
//                 onClick={deleteImage}
//                 disabled={isProcessing}
//               >
//                 {loadingDelete ? <span className="spinner-border spinner-border-sm" /> : <FaTrashAlt />}
//                 {loadingDelete ? "Deleting..." : "Remove"}
//               </button>

//               <button className="btn btn-light text-muted mt-2" onClick={handleCloseModal} disabled={isProcessing}>
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {popup.show && <PopupMessage type={popup.type} message={popup.message} onClose={() => setPopup({ ...popup, show: false })} />}

//       <style>{`
//         .custom-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(2px); display: flex; justify-content: center; align-items: center; z-index: 2000; cursor: pointer; }
//         .custom-modal { padding: 2rem; border-radius: 1rem; width: 90%; max-width: 320px; animation: popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); cursor: default; }
//         @keyframes popIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
//       `}</style>
//     </div>
//   );
// }
